import React, { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard,
  ClipboardCheck,
  AlertCircle,
  MessageSquare,
  TrendingUp,
  Activity,
  Clock3,
  BadgeCheck,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { inspectionsAPI, ncrAPI, documentsAPI } from "../../../services/api";
import { useProjectContext } from "../../../hooks/useProjectContext";
import MyIssuesView from "./MyIssuesView";

const normStatus = (s) => (s || "").toLowerCase();
const normSeverity = (s) => (s || "").toLowerCase();
const normDocumentTag = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const isRfiDocument = (doc) => {
  const tags = [
    normDocumentTag(doc?.document_type),
    normDocumentTag(doc?.category),
  ];
  return tags.some((tag) => tag === "rfi" || tag === "internal_rfi");
};

const isSubmittalDocument = (doc) => {
  const tags = [
    normDocumentTag(doc?.document_type),
    normDocumentTag(doc?.category),
  ];
  return tags.some(
    (tag) => tag === "submittal" || tag === "internal_submittal",
  );
};

const ISSUE_FIRST_ROLES = new Set(["foreman", "contractor", "client"]);

const formatRole = (raw) =>
  (raw || "Team member")
    .toString()
    .replaceAll("_", " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getUserName = (user) => {
  const full = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return full || user?.email || "there";
};

const KpiCard = ({
  label,
  total,
  highlight,
  highlightClass,
  icon: Icon,
  borderColorClass,
  iconBgClass,
  iconColorClass,
  lines = [],
  onClick,
  actionLabel,
}) => {
  const clickable = typeof onClick === "function";

  const content = (
    <>
      <div className={`absolute inset-y-0 left-0 w-1.5 ${borderColorClass}`} />

      <div className="relative flex h-full flex-col gap-3 p-4 pl-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500">{label}</p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-semibold text-gray-900">
                {total}
              </span>
              {highlight && (
                <span
                  className={`truncate text-xs font-semibold ${highlightClass}`}
                >
                  {highlight}
                </span>
              )}
            </div>
          </div>
          {Icon && (
            <div
              className={`flex items-center justify-center rounded-xl p-2 ${iconBgClass}`}
            >
              <Icon className={`h-5 w-5 ${iconColorClass}`} />
            </div>
          )}
        </div>

        {lines.length > 0 && (
          <div className="mt-1 space-y-0.5 text-[11px] leading-relaxed text-gray-600">
            {lines.map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </div>
        )}

        {clickable && (
          <div className="mt-auto pt-2 text-xs font-semibold text-orange-600">
            {actionLabel || `Open ${label}`} →
          </div>
        )}
      </div>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group relative h-full w-full overflow-hidden rounded-2xl border bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
        aria-label={actionLabel || `Open ${label}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className="relative h-full overflow-hidden rounded-2xl border bg-white shadow-sm">
      {content}
    </div>
  );
};

const ProjectEngineerDashboard = ({
  projectId,
  activeProject,
  currentUser,
  onNavigate,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await inspectionsAPI.listEngineeringIssues(projectId);
        if (mounted) setItems(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (mounted)
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              "Failed to load engineering issues.",
          );
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [projectId]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isOverdue = (item) => {
    if (!item?.due_date || normStatus(item.status) === "answered") return false;
    const due = new Date(`${item.due_date}T00:00:00`);
    return !Number.isNaN(due.getTime()) && due < today;
  };
  const counts = {
    total: items.length,
    open: items.filter((item) =>
      ["open", "forwarded"].includes(normStatus(item.status)),
    ).length,
    answered: items.filter((item) => normStatus(item.status) === "answered")
      .length,
    overdue: items.filter(isOverdue).length,
  };

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-slate-50 p-5 shadow-sm">
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-700 shadow-sm ring-1 ring-blue-100">
            <Activity className="h-3.5 w-3.5" /> Project Engineering Dashboard
          </div>
          <h2 className="mt-3 text-2xl font-black tracking-tight text-gray-950 sm:text-3xl">
            Welcome back, {getUserName(currentUser)}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="rounded-full border border-gray-200 bg-white px-3 py-1 font-medium text-gray-800">
              Project: {activeProject?.name || "—"}
            </span>
            <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 font-medium text-blue-800">
              Role: Project Engineer
            </span>
          </div>
          <p className="mt-3 max-w-3xl text-sm text-gray-600">
            This dashboard shows issues escalated for technical input and does
            not expose the internal inspection register.
          </p>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Engineering Issues"
            total={counts.total}
            highlight="Escalated to your engineering scope"
            highlightClass="text-blue-600"
            icon={Activity}
            borderColorClass="bg-blue-400"
            iconBgClass="bg-blue-50"
            iconColorClass="text-blue-500"
            lines={["Technical issues requiring engineering visibility"]}
            onClick={() => onNavigate?.("engineering-issues")}
            actionLabel="Open engineering issues"
          />
          <KpiCard
            label="Awaiting Input"
            total={counts.open}
            highlight="Needs engineering response"
            highlightClass="text-amber-600"
            icon={Clock3}
            borderColorClass="bg-amber-400"
            iconBgClass="bg-amber-50"
            iconColorClass="text-amber-500"
            lines={["Open or forwarded escalation items"]}
            onClick={() => onNavigate?.("engineering-issues")}
            actionLabel="Open awaiting input"
          />
          <KpiCard
            label="Answered"
            total={counts.answered}
            highlight="Engineering guidance provided"
            highlightClass="text-emerald-600"
            icon={BadgeCheck}
            borderColorClass="bg-emerald-400"
            iconBgClass="bg-emerald-50"
            iconColorClass="text-emerald-500"
            lines={["Completed engineering responses"]}
            onClick={() => onNavigate?.("engineering-issues")}
            actionLabel="Open answered issues"
          />
          <KpiCard
            label="Overdue Engineering"
            total={counts.overdue}
            highlight="Past due and still open"
            highlightClass="text-red-600"
            icon={AlertCircle}
            borderColorClass="bg-red-400"
            iconBgClass="bg-red-50"
            iconColorClass="text-red-500"
            lines={["Prioritize technical response"]}
            onClick={() => onNavigate?.("engineering-issues")}
            actionLabel="Open overdue engineering issues"
          />
        </div>
      )}
    </div>
  );
};

const ProjectDashboardView = ({ onNavigate }) => {
  const { activeProject, projectRole, currentUser } = useProjectContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [inspectionStats, setInspectionStats] = useState({
    total: 0,
    draft: 0,
    submitted: 0,
    approved: 0,
    rejected: 0,
  });

  const [ncrStats, setNcrStats] = useState({
    total: 0,
    open: 0,
    closed: 0,
    highCriticalOpen: 0,
  });

  const [rfiStats, setRfiStats] = useState({
    total: 0,
  });

  const [submittalStats, setSubmittalStats] = useState({
    total: 0,
  });

  const projectId = activeProject?.id;
  const isIssueFirstDashboard = ISSUE_FIRST_ROLES.has(normStatus(projectRole));
  const isProjectEngineerDashboard =
    normStatus(projectRole) === "project_engineer";

  const canReadInspections = useMemo(() => !!projectId, [projectId]);
  const canReadNcr = useMemo(() => !!projectId, [projectId]);
  const canReadDocuments = useMemo(() => !!projectId, [projectId]);

  useEffect(() => {
    if (!projectId || isIssueFirstDashboard || isProjectEngineerDashboard)
      return;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const [insRes, ncrRes, documentsRes] = await Promise.all([
          canReadInspections
            ? inspectionsAPI.listByProject(projectId)
            : Promise.resolve({ data: [] }),
          canReadNcr
            ? ncrAPI.listByProject(projectId)
            : Promise.resolve({ data: [] }),
          canReadDocuments
            ? documentsAPI.listByProject(projectId)
            : Promise.resolve({ data: [] }),
        ]);

        const inspectionsList = Array.isArray(insRes.data) ? insRes.data : [];
        const ncrList = Array.isArray(ncrRes.data) ? ncrRes.data : [];
        const documentsList = Array.isArray(documentsRes.data)
          ? documentsRes.data
          : [];

        const insStats = {
          total: inspectionsList.length,
          draft: 0,
          submitted: 0,
          approved: 0,
          rejected: 0,
        };
        inspectionsList.forEach((insp) => {
          const s = normStatus(insp.status);
          if (s === "draft") insStats.draft += 1;
          else if (s === "submitted") insStats.submitted += 1;
          else if (s === "approved") insStats.approved += 1;
          else if (s === "rejected") insStats.rejected += 1;
        });
        setInspectionStats(insStats);

        const nStats = {
          total: ncrList.length,
          open: 0,
          closed: 0,
          highCriticalOpen: 0,
        };
        ncrList.forEach((ncr) => {
          const s = normStatus(ncr.status);
          const sev = normSeverity(ncr.severity);

          if (s === "closed") {
            nStats.closed += 1;
            return;
          }

          // Every non-closed NCR is active. This remains correct when new
          // intermediate workflow statuses are introduced later.
          nStats.open += 1;
          if (sev === "high" || sev === "critical") {
            nStats.highCriticalOpen += 1;
          }
        });
        setNcrStats(nStats);

        // RFI/Submittal uploads are stored as Document rows, not workflow rows.
        // Filter the project document list once per card so a document tagged in
        // both category and document_type is still counted only once.
        setRfiStats({
          total: documentsList.filter(isRfiDocument).length,
        });

        setSubmittalStats({
          total: documentsList.filter(isSubmittalDocument).length,
        });
      } catch (e) {
        console.error(e);
        setError(
          e?.response?.data?.detail ||
            e.message ||
            "Failed to load project dashboard",
        );
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [
    projectId,
    isIssueFirstDashboard,
    isProjectEngineerDashboard,
    canReadInspections,
    canReadNcr,
    canReadDocuments,
  ]);

  if (!activeProject) return null;

  if (isProjectEngineerDashboard) {
    return (
      <ProjectEngineerDashboard
        projectId={projectId}
        activeProject={activeProject}
        currentUser={currentUser}
        onNavigate={onNavigate}
      />
    );
  }

  if (isIssueFirstDashboard) {
    const displayName = getUserName(currentUser);
    const roleLabel = formatRole(projectRole);

    return (
      <div className="min-w-0 space-y-5">
        <div className="relative overflow-hidden rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-slate-50 p-5 shadow-sm">
          <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-orange-200/40 blur-2xl" />
          <div className="absolute -bottom-16 left-1/3 h-32 w-32 rounded-full bg-blue-200/30 blur-2xl" />

          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide text-orange-700 shadow-sm ring-1 ring-orange-100">
                <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
              </div>

              <h2 className="mt-3 break-words text-2xl font-black tracking-tight text-gray-950 sm:text-3xl">
                Welcome back, {displayName}
              </h2>

              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span className="rounded-full border border-gray-200 bg-white/80 px-3 py-1 font-medium text-gray-800">
                  Project: {activeProject.name}
                </span>
                <span className="rounded-full border border-orange-100 bg-orange-50 px-3 py-1 font-medium text-orange-800">
                  Role: {roleLabel}
                </span>
                {activeProject.client ? (
                  <span className="rounded-full border border-gray-200 bg-white/80 px-3 py-1">
                    Client: {activeProject.client}
                  </span>
                ) : null}
              </div>

              <p className="mt-3 max-w-3xl break-words text-sm text-gray-600">
                Your dashboard is focused on assigned issues only. Use the cards
                below to filter open, pending, reopened, and overdue items, then
                respond directly from the queue.
              </p>
            </div>
          </div>
        </div>

        <MyIssuesView dashboardMode onNavigate={onNavigate} />
      </div>
    );
  }

  const {
    total: insTotal,
    draft,
    submitted,
    approved,
    rejected,
  } = inspectionStats;
  const {
    total: ncrTotal,
    open: ncrOpen,
    closed: ncrClosed,
    highCriticalOpen,
  } = ncrStats;
  const { total: rfiTotal } = rfiStats;
  const { total: subTotal } = submittalStats;

  const ncrHighlight =
    ncrOpen === 0
      ? "No open NCRs"
      : highCriticalOpen > 0
        ? "Requires attention"
        : "Open NCRs in progress";

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-orange-100 blur-2xl" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-orange-700 ring-1 ring-orange-100">
              <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
            </div>
            <h2 className="mt-3 break-words text-2xl font-black tracking-tight text-gray-950 sm:text-3xl">
              Welcome back, {getUserName(currentUser)}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 font-medium text-gray-800">
                Project: {activeProject.name || "—"}
              </span>
              <span className="rounded-full border border-orange-100 bg-orange-50 px-3 py-1 font-medium text-orange-800">
                Role: {formatRole(projectRole)}
              </span>
              <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 font-medium text-gray-800">
                Client: {activeProject.client || "—"}
              </span>
            </div>
            <p className="mt-4 max-w-4xl text-sm leading-6 text-gray-600">
              These KPIs are <strong>project-based</strong>. They show only
              records belonging to{" "}
              <span className="font-semibold text-gray-800">
                {activeProject.name}
              </span>
              , so you can quickly see the health of this project across
              inspections, NCRs, RFI uploads, and submittal uploads.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Inspections"
            total={insTotal}
            highlight={`${approved} approved`}
            highlightClass="text-emerald-600"
            icon={ClipboardCheck}
            borderColorClass="bg-emerald-400"
            iconBgClass="bg-emerald-50"
            iconColorClass="text-emerald-500"
            lines={[
              `Draft: ${draft} • Submitted: ${submitted}`,
              `Approved: ${approved} • Rejected: ${rejected}`,
            ]}
            onClick={() => onNavigate?.("inspections")}
            actionLabel="Open inspections"
          />
          <KpiCard
            label="Open NCRs"
            total={ncrOpen}
            highlight={ncrHighlight}
            highlightClass={
              ncrOpen === 0
                ? "text-emerald-600"
                : highCriticalOpen > 0
                  ? "text-red-600"
                  : "text-amber-600"
            }
            icon={AlertCircle}
            borderColorClass="bg-red-400"
            iconBgClass="bg-red-50"
            iconColorClass="text-red-500"
            lines={[
              `High / Critical open: ${highCriticalOpen}`,
              `Closed: ${ncrClosed} • Total in project: ${ncrTotal}`,
              "High / Critical NCRs surface under Urgent Items.",
            ]}
            onClick={() => onNavigate?.("ncr")}
            actionLabel="Open NCRs"
          />
          <KpiCard
            label="RFI Files"
            total={rfiTotal}
            highlight="Uploaded files"
            highlightClass="text-amber-600"
            icon={MessageSquare}
            borderColorClass="bg-amber-400"
            iconBgClass="bg-amber-50"
            iconColorClass="text-amber-500"
            lines={["RFI documents available in this project"]}
            onClick={() => onNavigate?.("documents")}
            actionLabel="Open RFI files"
          />
          <KpiCard
            label="Submittal Files"
            total={subTotal}
            highlight="Uploaded files"
            highlightClass="text-purple-600"
            icon={TrendingUp}
            borderColorClass="bg-purple-400"
            iconBgClass="bg-purple-50"
            iconColorClass="text-purple-500"
            lines={["Submittal documents available in this project"]}
            onClick={() => onNavigate?.("internal-submittals")}
            actionLabel="Open submittal files"
          />
        </div>
      )}
    </div>
  );
};

export default ProjectDashboardView;
